import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Card, Grid, Typography, Skeleton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Colors } from '../config/default';
import { generatePo } from '../constants/appConstants';
import { getTodaysDate } from '../services/common';

// Kept in sync with the row `mb` below; offsetHeight excludes margin, so the
// last row on a page needs it added back when measuring.
const ROW_MARGIN_BOTTOM = 8;

export default function GeneratePoCard({
  lineItemsPerPage,
  totalCardPages,
  height,
  width,
  cursor,
  onClick,
  estimateNumber,
  poNumberLoading,
  documentContentMapped,
  documentLineItemsMapped,
  parentHeight,
  show,
  textSize,
  isVista,
  onPaginate,
  // What the rendered card calls itself. Defaults to the PO wording the
  // generate-PO flows print; the side-by-side comparison passes a combined
  // PO/Subcontract heading, since the commitment it shows can be either.
  title = generatePo.PURCHASE_ORDER
}) {
  const extraLargeScreen = useMediaQuery('(min-width:2400px)');
  const largeScreen = useMediaQuery('(min-width:2000px) and (max-width:2400px)');
  const { DESCRIPTION, TOTAL, QUANTITY, UNIT_PRICE, UOM, SUBTOTAL, TAX_RATE, TAX_AMT } = generatePo;

  const items = documentLineItemsMapped || [];

  // Shared width rule so the hidden measuring card wraps text exactly like the
  // visible pages (identical width => identical line wrapping => identical row
  // heights).
  const cardWidth = width
    ? width
    : largeScreen
    ? '24vw'
    : extraLargeScreen
    ? '22vw'
    : { xs: '65vw', md: '30vw', lg: '26vw' };

  // ---- Dynamic pagination -------------------------------------------------
  // Rather than slicing a fixed number of rows per page (which clips rows when
  // a description wraps to several lines), we measure the real rendered height
  // of every row in a hidden mirror and greedily pack rows into each page up to
  // the page's available height. `pageChunks` is an array of [start, end) row
  // ranges — one entry per page.
  const visibleCardRef = useRef(null);
  const measureCardRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const rowRefs = useRef([]);
  const [pageChunks, setPageChunks] = useState(null);
  // The hidden mirror must wrap text at the SAME width as the visible card, or
  // row heights won't match. `width="auto"` cards size to their container, so we
  // copy the real rendered width off the visible page rather than re-deriving it.
  const [measureWidth, setMeasureWidth] = useState(null);

  const measure = useCallback(() => {
    const visible = visibleCardRef.current;
    if (visible) {
      const w = visible.getBoundingClientRect().width;
      if (w && Math.abs(w - (measureWidth || 0)) > 0.5) {
        setMeasureWidth(w); // mirror re-lays out at this width, then we re-measure
        return;
      }
    }

    const card = measureCardRef.current;
    const rows = rowRefs.current.filter(Boolean);
    const tableHeader = tableHeaderRef.current;
    if (!card || !rows.length || !tableHeader) {
      setPageChunks(null);
      return;
    }
    const cs = window.getComputedStyle(card);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padBottom = parseFloat(cs.paddingBottom) || 0;
    // Content-box height available for children on any page. A small safety gap
    // absorbs sub-pixel rounding so the last row's bottom border is never
    // re-clipped by the card's own overflow.
    const SAFETY = 8;
    const availableHeight = card.clientHeight - padTop - padBottom - SAFETY;
    if (availableHeight <= 0) {
      setPageChunks(null);
      return;
    }

    const rowsTop = rows[0].offsetTop; // distance (from card top) to first row
    const tableHeaderTop = tableHeader.offsetTop;
    // offsetTop already reflects an element's own top margin, so the difference
    // (rowsTop - tableHeaderTop) omits the column header's top margin — add it
    // back, since on a later page that margin sits above the header.
    const tableHeaderMarginTop = parseFloat(window.getComputedStyle(tableHeader).marginTop) || 0;
    // Space consumed above the first row: on page 0 that's the whole PO header
    // block + the column-header row; on later pages just the column-header row.
    const firstPageHead = rowsTop - padTop;
    const laterPageHead = rowsTop - tableHeaderTop + tableHeaderMarginTop;

    // Height of each row, including its bottom margin, from layout positions so
    // wrapped/multi-line descriptions are accounted for automatically.
    const rowHeights = rows.map((el, i) =>
      i < rows.length - 1 ? rows[i + 1].offsetTop - el.offsetTop : el.offsetHeight + ROW_MARGIN_BOTTOM
    );

    const chunks = [];
    let start = 0;
    let used = 0;
    let budget = availableHeight - firstPageHead;
    for (let i = 0; i < rowHeights.length; i++) {
      const h = rowHeights[i];
      // Break before this row if the current page already holds a row and the
      // next one would overflow. A single row taller than the page still gets
      // its own page (i === start) instead of looping forever.
      if (i > start && used + h > budget) {
        chunks.push([start, i]);
        start = i;
        used = 0;
        budget = availableHeight - laterPageHead;
      }
      used += h;
    }
    chunks.push([start, rowHeights.length]);

    setPageChunks((prev) =>
      prev && prev.length === chunks.length && prev.every((p, i) => p[0] === chunks[i][0] && p[1] === chunks[i][1])
        ? prev
        : chunks
    );
  }, [measureWidth]);

  // Signature of everything that can change row wrapping / page geometry; when
  // it changes we re-measure. useLayoutEffect measures before paint so the user
  // never sees the fallback layout flash.
  const layoutSignature = JSON.stringify([
    items.map((it) => [
      it?.description,
      it?.unit_price,
      it?.UOM,
      it?.qty,
      it?.total,
      it?.subtotal,
      it?.taxRate,
      it?.taxAmount
    ]),
    documentContentMapped?.map((c) => [c?.name, c?.value]),
    isVista,
    textSize,
    width || null,
    height || null,
    parentHeight || null,
    largeScreen,
    extraLargeScreen,
    !!show,
    estimateNumber || null
  ]);

  useLayoutEffect(() => {
    measure();
  }, [measure, layoutSignature]);

  // Responsive card widths are vw-based, so re-measure when the viewport (and
  // therefore wrapping) changes.
  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  // Effective page ranges: measured chunks once available, otherwise a
  // count-based fallback so the very first (pre-measure) render still shows
  // pages instead of nothing.
  let pages;
  if (pageChunks && pageChunks.length) {
    pages = pageChunks;
  } else if (items.length) {
    pages = [];
    const per = lineItemsPerPage || items.length;
    for (let s = 0; s < items.length; s += per) {
      pages.push([s, Math.min(s + per, items.length)]);
    }
  } else {
    pages = [[0, 0]];
  }

  // Report page count beyond the first page (same meaning as the old
  // `totalCardPages`) so callers can drive their PDF-capture loops.
  const extraPages = Math.max(0, pages.length - 1);
  useEffect(() => {
    onPaginate?.(extraPages);
  }, [extraPages, onPaginate]);

  const pageCardSx = {
    height: height,
    width: cardWidth,
    padding: '1.5em 2em',
    borderRadius: show ? '0px' : '1em',
    cursor: cursor,
    backgroundColor: '#ffffff'
  };

  const renderPoHeader = () => (
    <>
      <Grid
        item
        sx={{
          backgroundColor: Colors.LIGHT_TOURQUISE,
          padding: '.5rem',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
        <Typography
          sx={{
            // Sized to the title instead of a fixed 35% so a longer heading
            // ("PO/ Subcontract") stays on one line, capped at the card width.
            width: 'max-content',
            maxWidth: '100%',
            fontSize: largeScreen ? '30px' : extraLargeScreen ? '20px' : '18px',
            fontWeight: 'bolder'
          }}>
          {title}
        </Typography>
      </Grid>
      <div style={{ display: 'flex' }}>
        <Grid item xs={8} sx={{ marginTop: '.5rem' }}>
          <Typography
            sx={{
              backgroundColor: Colors.LIGHT_TOURQUISE,
              fontSize: textSize,
              width: 'max-content',
              padding: '0rem .2rem',
              marginBottom: '.5rem',
              fontWeight: 'bold',
              color: '#141414'
            }}>
            {`${title} Details`}
          </Typography>
          {!show && (estimateNumber || poNumberLoading) && (
            <Grid sx={{ display: 'flex', fontSize: textSize, color: '#141414', alignItems: 'center' }}>
              <div style={{ width: '40%' }}>
                <strong>PO # </strong>
              </div>
              <div>
                {poNumberLoading ? (
                  <Skeleton variant="text" width={80} sx={{ bgcolor: 'rgba(0,0,0,0.12)' }} />
                ) : (
                  estimateNumber
                )}
              </div>
            </Grid>
          )}
          {documentContentMapped?.map(
            (item) =>
              item?.name !== 'Vendor' &&
              !(isVista && item?.name === 'Created By') && (
                <Grid
                  sx={{
                    display: 'flex',
                    fontSize: textSize,
                    marginTop: '.2rem',
                    color: '#141414'
                  }}>
                  <div style={{ width: '40%' }}>
                    <strong>{item?.name}</strong>
                  </div>
                  <div>{item?.name === 'Date' ? getTodaysDate() : item?.value}</div>
                </Grid>
              )
          )}
        </Grid>
        <Grid item xs={4} sx={{ marginTop: '.5rem' }}>
          <Typography
            sx={{
              backgroundColor: Colors.LIGHT_TOURQUISE,
              fontSize: textSize,
              width: 'max-content',
              padding: '0rem .2rem',
              marginBottom: '.5rem',
              fontWeight: 'bold',
              color: '#141414'
            }}>
            Order To
          </Typography>
          {documentContentMapped?.map(
            (item) =>
              item?.name === 'Vendor' && (
                <Grid sx={{ fontSize: textSize, marginTop: '.2rem', color: '#141414' }}>
                  <div style={{ marginTop: '.3rem', overflowWrap: 'break-word' }}>{item?.value}</div>
                </Grid>
              )
          )}
          {isVista && (
            <>
              <Typography
                sx={{
                  backgroundColor: Colors.LIGHT_TOURQUISE,
                  fontSize: textSize,
                  width: 'max-content',
                  padding: '0rem .2rem',
                  marginTop: '.75rem',
                  marginBottom: '.5rem',
                  fontWeight: 'bold',
                  color: '#141414'
                }}>
                Order By
              </Typography>
              <Grid sx={{ fontSize: textSize, marginTop: '.2rem', color: '#141414' }}>
                <div style={{ marginTop: '.3rem', overflowWrap: 'break-word' }}>
                  {documentContentMapped?.find((item) => item?.name === 'Created By')?.value || '-'}
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </>
  );

  const renderTableHeader = (ref) => (
    <Grid
      ref={ref}
      xs={12}
      container
      sx={{
        fontWeight: 'bold',
        fontSize: textSize,
        mt: '1em',
        mb: '8px',
        padding: '.1rem',
        backgroundColor: Colors.LIGHT_TOURQUISE,
        color: '#141414',
        ...(isVista && {
          overflowWrap: 'anywhere',
          '& > *': { pr: '6px', boxSizing: 'border-box' }
        })
      }}>
      <Grid xs={isVista ? 2.5 : 4}>{DESCRIPTION}</Grid>
      <Grid xs={isVista ? 1.5 : 2.5}>{UNIT_PRICE}</Grid>
      <Grid xs={isVista ? 1 : 1.5}>{UOM}</Grid>
      <Grid xs={isVista ? 1 : 2}>{isVista ? 'Qty' : QUANTITY}</Grid>
      {isVista && <Grid xs={1.5}>{SUBTOTAL}</Grid>}
      {isVista && <Grid xs={1}>{TAX_RATE}</Grid>}
      {isVista && <Grid xs={1.5}>{TAX_AMT}</Grid>}
      <Grid xs={2}>{TOTAL}</Grid>
    </Grid>
  );

  const renderRow = (row, key, ref) => (
    <Grid
      ref={ref}
      key={key}
      xs={12}
      container
      sx={{
        fontSize: textSize,
        borderBottom: '1px solid black',
        mb: '8px',
        color: '#141414',
        wordBreak: 'break-word',
        ...(isVista && { '& > *': { pr: '6px', boxSizing: 'border-box' } })
      }}>
      <Grid xs={isVista ? 2.5 : 4}>{row?.description}</Grid>
      <Grid xs={isVista ? 1.5 : 2.5}>{row?.unit_price}</Grid>
      <Grid xs={isVista ? 1 : 1.5}>{row?.UOM}</Grid>
      <Grid xs={isVista ? 1 : 2}>{row?.qty}</Grid>
      {isVista && <Grid xs={1.5}>{row?.subtotal}</Grid>}
      {isVista && <Grid xs={1}>{row?.taxRate || '-'}</Grid>}
      {isVista && <Grid xs={1.5}>{row?.taxAmount}</Grid>}
      <Grid xs={2}>{row?.total}</Grid>
    </Grid>
  );

  return (
    <Grid
      sx={{
        height: parentHeight || 'calc( 67vh + 3em )',
        overflowY: items.length >= (lineItemsPerPage || items.length) ? 'auto' : 'hidden',
        paddingRight: show ? 'auto' : '10px'
      }}>
      {/* Visible pages */}
      {pages.map((range, pageIndex) => {
        const cardId = `Po${pageIndex}`;
        const isFirst = pageIndex === 0;
        return (
          <Card
            key={cardId}
            id={cardId}
            ref={isFirst ? visibleCardRef : undefined}
            onClick={onClick}
            sx={{ ...pageCardSx, ...(isFirst ? {} : { mt: 1 }) }}>
            {isFirst && renderPoHeader()}
            {items.length === 0 ? (
              ''
            ) : (
              <Grid>
                {renderTableHeader()}
                {items.slice(range[0], range[1]).map((row, idx) => renderRow(row, idx))}
              </Grid>
            )}
          </Card>
        );
      })}

      {/* Hidden mirror used only for measuring true row heights. Positioned off
          screen and never given a `Po*` id, so document capture ignores it. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: '-100000px',
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none'
        }}>
        <Card
          ref={measureCardRef}
          sx={{
            ...pageCardSx,
            position: 'relative',
            boxSizing: 'border-box',
            ...(measureWidth ? { width: `${measureWidth}px` } : {})
          }}>
          {renderPoHeader()}
          {items.length > 0 && (
            <Grid>
              {renderTableHeader(tableHeaderRef)}
              {items.map((row, idx) =>
                renderRow(row, idx, (el) => {
                  rowRefs.current[idx] = el;
                })
              )}
            </Grid>
          )}
        </Card>
      </div>
    </Grid>
  );
}
